/**
 * List/nested-list JSX generation for form fields
 */

import type { SchemaField } from '@core-engine/schema/types.js'
import { quotePropertyName, singularize, toPascalCase } from '@core-engine/utils/string.js'
import { formItemProps, formLabel } from '@next-adapter/generators/form/form-shared.js'

type PathPart = string | { expression: string }

function labelWithDescription(labelJSX: string, descriptionJSX: string, indent: string): string {
  if (!descriptionJSX) return labelJSX
  return `<div className="flex flex-col gap-1">
${indent}  ${labelJSX}
${indent}  ${descriptionJSX}
${indent}</div>`
}

function emptyNestedFieldValue(field: SchemaField): string {
  if ((field.type === 'group' || field.type === 'section') && field.fields?.length) {
    return objectValueForFields(field.fields)
  }
  if (field.type === 'list' || field.type === 'gallery') return '[]'
  if (field.type === 'relationship' && field.multiple) return '[]'
  if (field.type === 'select' && field.multiple) return '[]'
  if (field.type === 'boolean') return 'false'
  if (field.type === 'number' || field.type === 'decimal') return field.required ? '0' : 'undefined'
  return "''"
}

function objectEntriesForFields(fields: SchemaField[]): string[] {
  return fields.flatMap((field) => {
    const entries = [`${quotePropertyName(field.name)}: ${emptyNestedFieldValue(field)}`]
    if (field.hasIcon) {
      entries.push(`${quotePropertyName(`${field.name}Icon`)}: ''`)
    }
    return entries
  })
}

function objectValueForFields(fields: SchemaField[]): string {
  return `{${objectEntriesForFields(fields).join(', ')}}`
}

function safeIdentifier(value: string, fallback: string): string {
  const pascal = toPascalCase(value).replace(/[^A-Za-z0-9_$]/g, '')
  const base = pascal ? `${pascal.charAt(0).toLowerCase()}${pascal.slice(1)}` : fallback
  return /^[A-Za-z_$]/.test(base) ? base : fallback
}

function pathExpression(path: PathPart[]): string {
  return `\`${path
    .map((part) => (typeof part === 'string' ? part : `\${${part.expression}}`))
    .join('.')}\``
}

function pathNameProp(path: PathPart[]): string {
  if (path.every((part) => typeof part === 'string')) {
    return `"${path.map((part) => part as string).join('.')}"`
  }
  return `{${pathExpression(path)}}`
}

function findTitlePath(fields: SchemaField[]): string[] | null {
  const stringTypes = ['string', 'varchar', 'text']
  const preferredNames = ['title', 'name', 'label']

  for (const preferredName of preferredNames) {
    const field = fields.find((f) => f.name === preferredName && stringTypes.includes(f.type))
    if (field) return [field.name]
  }

  const directStringField = fields.find((f) => stringTypes.includes(f.type))
  if (directStringField) return [directStringField.name]

  for (const field of fields) {
    if ((field.type === 'group' || field.type === 'section') && field.fields?.length) {
      const nestedPath = findTitlePath(field.fields)
      if (nestedPath) return [field.name, ...nestedPath]
    }
  }

  return null
}

function requiredLeafPaths(fields: SchemaField[], prefix: string[] = []): string[] {
  return fields.flatMap((field) => {
    if (field.hidden) return []

    const path = [...prefix, field.name]

    if ((field.type === 'group' || field.type === 'section') && field.fields?.length) {
      return requiredLeafPaths(field.fields, path)
    }

    if (field.required) return [path.join('.')]

    return []
  })
}

function validationTriggerExpression(basePath: string, requiredPaths: string[]): string {
  if (requiredPaths.length === 0) return `\`${basePath}.\${expandedIndex}\` as never`

  const paths = requiredPaths.map((path) => `\`${basePath}.\${expandedIndex}.${path}\``).join(', ')
  return `[${paths}] as never`
}

function renderTextInputField(
  field: SchemaField,
  indent: string,
  label: string,
  nestedHint: string,
  path: PathPart[]
): string {
  return `${indent}<FormField
${indent}  control={form.control}
${indent}  name=${pathNameProp(path)}
${indent}  render={({ field: formField }) => (
${indent}    <FormItem${formItemProps(field)}>
${indent}      ${labelWithDescription(formLabel(field, label), nestedHint, `${indent}      `)}
${indent}      <FormControl>
${indent}        <Input placeholder="Enter ${label.toLowerCase()}" disabled={isPending} {...formField} />
${indent}      </FormControl>
${indent}      <FormMessage />
${indent}    </FormItem>
${indent}  )}
${indent}/>`
}

function renderNestedField(
  field: SchemaField,
  indent: string,
  path: PathPart[],
  depth: number
): string {
  const nestedLabel = field.label || field.name
  const nestedHint = field.hint ? `<FormDescription>${field.hint}</FormDescription>` : ''

  if ((field.type === 'group' || field.type === 'section') && field.fields?.length) {
    const groupFields = field.fields
      .map((child) => renderNestedField(child, `${indent}    `, [...path, child.name], depth))
      .filter(Boolean)
      .join('\n')
    if (!groupFields) return ''

    const heading =
      nestedLabel && nestedLabel !== field.name
        ? `${indent}<h3 className="text-base font-medium">${nestedLabel}</h3>\n`
        : ''
    const columns = field.columns || 1
    return `${heading}${indent}<div className="grid ${columns > 1 ? `grid-cols-${columns}` : 'grid-cols-1'} gap-2">
${groupFields}
${indent}</div>`
  }

  if (field.type === 'list' && field.fields?.length) {
    return renderNestedObjectListField(field, indent, nestedLabel, path, depth)
  }

  if (field.type === 'list') {
    const hideLabelProp = field.label ? '' : `\n${indent}  hideLabel`
    const descriptionProp = field.hint
      ? `\n${indent}  description={${JSON.stringify(field.hint)}}`
      : ''
    return `${indent}<DynamicListField
${indent}  name={${pathExpression(path)}}
${indent}  label="${nestedLabel}"${hideLabelProp}${descriptionProp}
${indent}  disabled={isPending}${field.maxItems ? `\n${indent}  maxItems={${field.maxItems}}` : ''}
${indent}  placeholder="Enter value"
${indent}/>`
  }

  if (field.type === 'boolean') {
    return `${indent}<FormField
${indent}  control={form.control}
${indent}  name=${pathNameProp(path)}
${indent}  render={({ field: formField }) => (
${indent}    <FormItem${formItemProps(field, 'flex flex-row items-start space-x-3 space-y-0')}>
${indent}      <FormControl>
${indent}        <Checkbox checked={formField.value} onCheckedChange={formField.onChange} disabled={isPending} />
${indent}      </FormControl>
${indent}      ${labelWithDescription(`<FormLabel>${nestedLabel}</FormLabel>`, nestedHint, `${indent}      `)}
${indent}    </FormItem>
${indent}  )}
${indent}/>`
  }

  if (field.type === 'image' || field.type === 'video' || field.type === 'media') {
    const acceptProp =
      field.type === 'image'
        ? ' accept="image/*"'
        : field.type === 'video'
          ? ' accept="video/*"'
          : ''
    return `${indent}<FormField
${indent}  control={form.control}
${indent}  name=${pathNameProp(path)}
${indent}  render={({ field: formField }) => (
${indent}    <FormItem${formItemProps(field)}>
${indent}      ${labelWithDescription(formLabel(field, nestedLabel), nestedHint, `${indent}      `)}
${indent}      <FormControl>
${indent}        <MediaField value={formField.value} onChange={formField.onChange} onBlur={formField.onBlur} disabled={isPending}${acceptProp} />
${indent}      </FormControl>
${indent}      <FormMessage />
${indent}    </FormItem>
${indent}  )}
${indent}/>`
  }

  if (field.type === 'icon') {
    return `${indent}<FormField
${indent}  control={form.control}
${indent}  name=${pathNameProp(path)}
${indent}  render={({ field: formField }) => (
${indent}    <FormItem${formItemProps(field)}>
${indent}      ${labelWithDescription(formLabel(field, nestedLabel), nestedHint, `${indent}      `)}
${indent}      <FormControl>
${indent}        <IconPicker value={formField.value} onValueChange={formField.onChange} triggerPlaceholder="Select ${nestedLabel.toLowerCase()}" />
${indent}      </FormControl>
${indent}      <FormMessage />
${indent}    </FormItem>
${indent}  )}
${indent}/>`
  }

  if (field.type === 'number' || field.type === 'decimal') {
    const stepProp = field.type === 'decimal' ? ' step="any"' : ''
    const emptyValue = field.required ? 'undefined' : 'null'
    return `${indent}<FormField
${indent}  control={form.control}
${indent}  name=${pathNameProp(path)}
${indent}  render={({ field: formField }) => (
${indent}    <FormItem${formItemProps(field)}>
${indent}      ${labelWithDescription(formLabel(field, nestedLabel), nestedHint, `${indent}      `)}
${indent}      <FormControl>
${indent}        <Input
${indent}          type="number"${stepProp}
${indent}          placeholder="Enter ${nestedLabel.toLowerCase()}"
${indent}          disabled={isPending}
${indent}          value={formField.value ?? ''}
${indent}          onChange={(event) => {
${indent}          const nextValue = event.target.value === '' ? ${emptyValue} : event.target.valueAsNumber
${indent}          const normalizedValue = Number.isNaN(nextValue) ? ${emptyValue} : nextValue
${indent}          formField.onChange(normalizedValue)
${indent}          }}
${indent}          onBlur={formField.onBlur}
${indent}          name={formField.name}
${indent}          ref={formField.ref}
${indent}        />
${indent}      </FormControl>
${indent}      <FormMessage />
${indent}    </FormItem>
${indent}  )}
${indent}/>`
  }

  return renderTextInputField(field, indent, nestedLabel, nestedHint, path)
}

function renderNestedObjectListField(
  field: SchemaField,
  indent: string,
  label: string,
  path: PathPart[],
  depth: number
): string {
  const singularLabel = singularize(label)
  const itemIndexVar = `${safeIdentifier(field.name, 'nestedList')}Index${depth}`
  const titlePath = findTitlePath(field.fields ?? [])
  const titlePathSuffix = titlePath ? `.${titlePath.join('.')}` : ''
  const renderTitleProp = titlePath
    ? `\n${indent}  renderTitle={(${itemIndexVar}) =>
${indent}    String(form.watch(\`${path
        .map((part) => (typeof part === 'string' ? part : `\${${part.expression}}`))
        .join(
          '.'
        )}.\${${itemIndexVar}}${titlePathSuffix}\` as never) || \`${singularLabel} \${${itemIndexVar} + 1}\`)
${indent}  }`
    : ''
  const nestedFieldsJSX = (field.fields ?? [])
    .map((child) =>
      renderNestedField(
        child,
        `${indent}      `,
        [...path, { expression: itemIndexVar }, child.name],
        depth + 1
      )
    )
    .filter(Boolean)
    .join('\n')
  const defaultItem = objectValueForFields(field.fields ?? [])
  const descriptionProp = field.hint
    ? `\n${indent}  description={${JSON.stringify(field.hint)}}`
    : ''
  const hideLabelProp = field.label ? '' : `\n${indent}  hideLabel`
  const maxItemsProp = field.maxItems ? `\n${indent}  maxItems={${field.maxItems}}` : ''
  const validatePaths = requiredLeafPaths(field.fields ?? [])
  const validatePathsProp =
    validatePaths.length > 0 ? `\n${indent}  validatePaths={${JSON.stringify(validatePaths)}}` : ''

  return `${indent}<NestedObjectListField
${indent}  name={${pathExpression(path)}}
${indent}  label={${JSON.stringify(label)}}
${indent}  singularLabel={${JSON.stringify(singularLabel)}}
${indent}  defaultValue={${defaultItem}}
${indent}  disabled={isPending}${hideLabelProp}${descriptionProp}${maxItemsProp}${validatePathsProp}${renderTitleProp}
${indent}>
${indent}  {(${itemIndexVar}) => (
${indent}    <>
${nestedFieldsJSX}
${indent}    </>
${indent}  )}
${indent}</NestedObjectListField>`
}

export function renderListField(field: SchemaField, indent: string, label: string): string {
  const listHintJSX = field.hint
    ? `<FormDescription className="text-muted-foreground">${field.hint}</FormDescription>`
    : ''

  // Simple string list — use DynamicListField
  if (!field.fields || field.fields.length === 0) {
    const hideLabelProp = field.label ? '' : `\n${indent}  hideLabel`
    const descriptionProp = field.hint
      ? `\n${indent}  description={${JSON.stringify(field.hint)}}`
      : ''
    return `${indent}<DynamicListField
${indent}  name="${field.name}"
${indent}  label="${label}"${hideLabelProp}${descriptionProp}
${indent}  disabled={isPending}${field.maxItems ? `\n${indent}  maxItems={${field.maxItems}}` : ''}
${indent}  placeholder="Enter value"
${indent}/>`
  }

  // Nested fields list — useFieldArray + Accordion
  const singularLabel = singularize(label)
  const pascalFieldName = toPascalCase(field.name)

  const titlePath = findTitlePath(field.fields)
  const accordionTitle = titlePath
    ? `{String(form.watch(\`${field.name}.\${index}.${titlePath!.join('.')}\` as never) || \`${singularLabel} \${index + 1}\`)}`
    : `${singularLabel} {index + 1}`

  // Generate nested field JSX
  const nestedFieldsJSX = field.fields
    .map((nf) =>
      renderNestedField(
        nf,
        `${indent}                  `,
        [field.name, { expression: 'index' }, nf.name],
        0
      )
    )
    .join('\n')
  const defaultItem = objectValueForFields(field.fields)
  const validateExpandedItemExpression = validationTriggerExpression(
    field.name,
    requiredLeafPaths(field.fields)
  )

  return `${indent}<FormItem${formItemProps(field)}>
${indent}  {${field.name}FieldArray.fields.length === 0 && (
${indent}    <Card>
${indent}      <CardContent className="flex flex-col items-center justify-center bg-background gap-3">
${indent}        <p>No ${label.toLowerCase()} added yet.</p>
${indent}        <Button
${indent}          type="button"
${indent}          variant="outline"
${indent}          onClick={async () => {
${indent}            if (${field.name}Expanded) {
${indent}              const expandedIndex = Number(${field.name}Expanded.replace('item-', '')) - 1
${indent}              if (expandedIndex >= 0) {
${indent}                const isExpandedItemValid = await form.trigger(${validateExpandedItemExpression})
${indent}                if (!isExpandedItemValid) return
${indent}              }
${indent}            }
${indent}            ${field.name}FieldArray.append(${defaultItem})
${indent}            set${pascalFieldName}Expanded(\`item-\${${field.name}FieldArray.fields.length + 1}\`)
${indent}          }}
${indent}          disabled={isPending}
${indent}        >
${indent}          <Plus className="size-3" />
${indent}          Add ${singularLabel}
${indent}        </Button>
${indent}      </CardContent>
${indent}    </Card>
${indent}  )}

${indent}  {${field.name}FieldArray.fields.length > 0 && (
${indent}    <div className="space-y-4">
${indent}      <div className="flex items-center justify-between gap-3">
${indent}        <FormField
${indent}          control={form.control}
${indent}          name="${field.name}"
${indent}          render={() => (
${indent}            <FormItem className="flex flex-col gap-1 space-y-0">
${indent}              <FormLabel className="${field.label ? 'text-foreground' : 'sr-only'}">${label}</FormLabel>
${listHintJSX ? `${indent}              ${listHintJSX}\n` : ''}${indent}            </FormItem>
${indent}          )}
${indent}        />
${indent}        <Button
${indent}          type="button"
${indent}          variant="outline"
${indent}          onClick={async () => {
${indent}            if (${field.name}Expanded) {
${indent}              const expandedIndex = Number(${field.name}Expanded.replace('item-', '')) - 1
${indent}              if (expandedIndex >= 0) {
${indent}                const isExpandedItemValid = await form.trigger(${validateExpandedItemExpression})
${indent}                if (!isExpandedItemValid) return
${indent}              }
${indent}            }
${indent}            ${field.name}FieldArray.append(${defaultItem})
${indent}            set${pascalFieldName}Expanded(\`item-\${${field.name}FieldArray.fields.length + 1}\`)
${indent}          }}
${indent}          disabled={isPending${field.maxItems ? ` || ${field.name}FieldArray.fields.length >= ${field.maxItems}` : ''}}
${indent}        >
${indent}          <Plus className="size-3" />
${indent}          Add ${singularLabel}
${indent}        </Button>
${indent}      </div>

${indent}      <Accordion
${indent}        type="single"
${indent}        collapsible
${indent}        value={${field.name}Expanded}
${indent}        onValueChange={async (nextValue) => {
${indent}          if (${field.name}Expanded && nextValue && nextValue !== ${field.name}Expanded) {
${indent}            const expandedIndex = Number(${field.name}Expanded.replace('item-', '')) - 1
${indent}            if (expandedIndex >= 0) {
${indent}              const isExpandedItemValid = await form.trigger(${validateExpandedItemExpression})
${indent}              if (!isExpandedItemValid) return
${indent}            }
${indent}          }
${indent}          set${pascalFieldName}Expanded(nextValue)
${indent}        }}
${indent}      >
${indent}        {${field.name}FieldArray.fields.map((item, index) => (
${indent}          <AccordionItem key={item.id} value={\`item-\${index + 1}\`}>
${indent}            <div className="relative">
${indent}              <AccordionTrigger className="flex items-center w-full gap-2 py-2 min-h-12">
${indent}                <span className="w-full pr-16">${accordionTitle}</span>
${indent}              </AccordionTrigger>
${indent}              <Button
${indent}                type="button"
${indent}                variant="ghost"
${indent}                size="icon-sm"
${indent}                className="absolute right-10 top-1/2 -translate-y-1/2 shrink-0"
${indent}                aria-label="Remove ${singularLabel}"
${indent}                onClick={() => {
${indent}                  if (isPending) return
${indent}                  ${field.name}FieldArray.remove(index)
${indent}                }}
${indent}                disabled={isPending}
${indent}              >
${indent}                <X />
${indent}              </Button>
${indent}            </div>
${indent}            <AccordionContent>
${indent}              <div className="space-y-4 pt-4 border-t border-border">
${nestedFieldsJSX}
${indent}              </div>
${indent}            </AccordionContent>
${indent}          </AccordionItem>
${indent}        ))}
${indent}      </Accordion>
${indent}    </div>
${indent}  )}
${indent}</FormItem>`
}
